import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import type { components } from '@primum/api-types';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { fetchClient } from '@/util/fetchClient';

type Schemas = components['schemas'];

/**
 * Per-user state for dismissible in-app callouts — the server source of truth
 * that replaces device-local prompt storage so a dismissal follows the user
 * across devices instead of resetting per device/browser.
 *
 * One uniform model every consumer conforms to, expressed as orthogonal
 * lifecycle primitives rather than per-callout columns: shown/dismissed counts
 * and timestamps, a snooze (`deferredUntil`), and a resolution (`resolvedAt`).
 * Stages (e.g. a nudge that de-escalates modal → banner → silenced) are derived
 * from `dismissedCount`/`resolvedAt` on the client, not stored as an enum, so a
 * new callout needs no schema change.
 *
 * The table gates ("should we show this now?") — it is not impression
 * analytics. Fire `shown` on a real surfacing (throttled), never every render.
 * When a callout's resolution is a live feature value (e.g. SMS-on in
 * notification preferences) gate on that value, not on `resolvedAt`.
 */
export type UserCallout = Schemas['UserCallout'];

export type CalloutAction = Schemas['CalloutActionRequest']['action'];

/**
 * Stable callout identifiers shared with the backend. Each value must match the
 * `key` the server stores for that callout exactly, or the row never resolves —
 * renaming one orphans every existing row for the old key.
 *
 * Naming: `<subject>_<detail?>_<kind>`, a snake_case value whose constant name is
 * its exact SCREAMING_SNAKE_CASE.
 *
 * - `<subject>` leads the key: the feature, area, or audience the callout belongs
 *   to (`expert`, `inquiry`, `fellows_corner`). Subject-first so keys sort into
 *   per-feature groups. Skip an audience prefix when the feature already scopes it
 *   (`fellows_corner_intro`, not `fellow_fellows_corner_intro`).
 * - `<kind>` is the trailing token — the callout's type. Kind is a *suffix*, not a
 *   prefix, so the subject stays the primary sort key. Established kinds: `intro`
 *   (one-time introduction, resolved once acknowledged) and `sms` (SMS opt-in
 *   prompt). Prefer `intro` over aging labels like `new_feature`. Add a new kind
 *   only for a genuinely new type, and keep it last.
 */
export const CALLOUT_KEYS = {
  EXPERT_FIRST_MESSAGE_SMS: 'expert_first_message_sms',
  INQUIRY_SUCCESS_SMS: 'inquiry_success_sms',
  INQUIRY_SUCCESS_PUSH: 'inquiry_success_push',
  ONBOARDING_TUTORIAL_INTRO: 'onboarding_tutorial_intro',
  EXPERT_ON_YOUR_HONOR_INTRO: 'expert_on_your_honor_intro',
  EXPERT_REFERENCE_ASSISTANT_INTRO: 'expert_reference_assistant_intro',
  FELLOWS_CORNER_INTRO: 'fellows_corner_intro',
  INBOX_QUESTION_GROUP_CARDS_INTRO: 'inbox_case_cards_intro',
  RESEARCH_TUTORIAL_INTRO: 'research_tutorial_intro',
  REPLY_DRAFT_INTRO: 'reply_draft_intro',
  EXPERT_AWAY_CALENDAR_INTRO: 'expert_away_calendar_intro',
  EXPERT_AWAY_CALENDAR_SHEET_HINT: 'expert_away_calendar_sheet_hint',
} as const;

export type CalloutKey = (typeof CALLOUT_KEYS)[keyof typeof CALLOUT_KEYS];

async function getUserCallouts(): Promise<UserCallout[]> {
  const { data } = await fetchClient.GET('/v2/user-callouts');

  return data?.data ?? [];
}

/**
 * Apply one lifecycle transition to the authenticated user's `(key)` callout,
 * creating the row on first touch. `snooze` requires `deferredUntil`; the
 * server increments counters atomically so concurrent devices can't corrupt
 * them. Returns the updated row.
 */
export async function applyCalloutAction(
  key: string,
  action: CalloutAction,
  deferredUntil?: string
): Promise<UserCallout> {
  const { data } = await fetchClient.POST('/v2/user-callouts/{key}', {
    params: { path: { key } },
    body: { action, ...(deferredUntil ? { deferredUntil } : {}) },
  });

  if (!data?.data) {
    throw new Error('Callout action returned empty response');
  }

  return data.data;
}

/** The user's callout row for `key`, or `undefined` if it has never been touched. */
export function selectCallout(
  callouts: UserCallout[] | undefined,
  key: CalloutKey
): UserCallout | undefined {
  return callouts?.find((callout) => callout.key === key);
}

/**
 * Fail-closed gate for a show-once-then-resolve callout: suppress until the
 * callouts query has positively loaded AND the row is unresolved. While the
 * query is loading or errored (`calloutsLoaded` false) the callout is treated
 * as suppressing, so an outage never re-surfaces a prompt the user already
 * resolved. For staged callouts that also de-escalate, derive the stage instead.
 */
export function shouldSuppressCallout(
  calloutsLoaded: boolean,
  callout: UserCallout | undefined
): boolean {
  return !calloutsLoaded || Boolean(callout?.resolvedAt);
}

export function getUserCalloutsQueryKey(userId?: string) {
  return ['userCallouts', userId] as const;
}

/**
 * The client reads every callout row once on load and decides per callout
 * whether to show, snooze, or stay quiet. Scoped per `userId` so an account
 * switch can't read another user's dismissals from cache.
 */
export function getUserCalloutsQueryOptions(userId?: string) {
  return queryOptions({
    queryKey: getUserCalloutsQueryKey(userId),
    queryFn: getUserCallouts,
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
    enabled: shouldFetchUserScoped(userId),
  });
}
